import {
  Image as LucideImage,
  Video as LucideVideo,
  Music as LucideMusic,
  File as LucideFile,
  FileText as LucideFileText,
  Archive as LucideArchive,
  Code as LucideCode,
  FileImage as LucidePresentation,
  Grid3x3 as LucideFileSpreadsheet,
  Headphones as LucideHeadphones,
  Contact as LucideContact,
} from "lucide-react";

const MEDIA_TYPES = {
  // Imagens
  image: {
    icon: LucideImage,
    label: "Imagem",
    mimetypes: [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
      "image/svg+xml",
      "image/bmp",
      "image/tiff",
      "image/ico",
      "image/heic",
      "image/heif",
    ],
    extensions: [
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
      ".webp",
      ".svg",
      ".bmp",
      ".tiff",
      ".ico",
      ".heic",
      ".heif",
    ],
  },

  // Vídeos
  video: {
    icon: LucideVideo,
    label: "Vídeo",
    mimetypes: [
      "video/mp4",
      "video/avi",
      "video/mov",
      "video/wmv",
      "video/flv",
      "video/webm",
      "video/mkv",
      "video/3gp",
      "video/m4v",
      "video/quicktime",
    ],
    extensions: [
      ".mp4",
      ".avi",
      ".mov",
      ".wmv",
      ".flv",
      ".webm",
      ".mkv",
      ".3gp",
      ".m4v",
    ],
  },

  // Áudio
  audio: {
    icon: LucideMusic,
    label: "Áudio",
    mimetypes: [
      "audio/mpeg",
      "audio/mp3",
      "audio/wav",
      "audio/ogg",
      "audio/aac",
      "audio/flac",
      "audio/wma",
      "audio/m4a",
      "audio/opus",
      "audio/webm",
    ],
    extensions: [
      ".mp3",
      ".wav",
      ".ogg",
      ".aac",
      ".flac",
      ".wma",
      ".m4a",
      ".opus",
    ],
  },

  // Áudio
  voice: {
    icon: LucideHeadphones,
    label: "Gravação de Voz",
    mimetypes: ["audio/ogg; codecs=opus", "audio/webm", "audio/wav"],
    extensions: [".ogg", ".webm"],
  },

  // Documentos de texto
  document: {
    icon: LucideFileText,
    label: "Documento",
    mimetypes: [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
      "application/rtf",
    ],
    extensions: [".pdf", ".doc", ".docx", ".txt", ".rtf"],
  },

  // Planilhas
  spreadsheet: {
    icon: LucideFileSpreadsheet,
    label: "Planilha",
    mimetypes: [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
    ],
    extensions: [".xls", ".xlsx", ".csv"],
  },

  // Apresentações
  presentation: {
    icon: LucidePresentation,
    label: "Apresentação",
    mimetypes: [
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ],
    extensions: [".ppt", ".pptx"],
  },

  // Arquivos comprimidos
  archive: {
    icon: LucideArchive,
    label: "Arquivo Comprimido",
    mimetypes: [
      "application/zip",
      "application/x-rar-compressed",
      "application/x-7z-compressed",
      "application/x-tar",
      "application/gzip",
    ],
    extensions: [".zip", ".rar", ".7z", ".tar", ".gz"],
  },

  // Código
  code: {
    icon: LucideCode,
    label: "Código",
    mimetypes: [
      "text/javascript",
      "text/css",
      "text/html",
      "application/json",
      "text/xml",
    ],
    extensions: [
      ".js",
      ".css",
      ".html",
      ".json",
      ".xml",
      ".php",
      ".py",
      ".java",
      ".cpp",
      ".c",
      ".cs",
    ],
  },

  // Contatos
  contact: {
    icon: LucideContact,
    label: "Contato",
    mimetypes: ["text/vcard", "text/x-vcard"],
    extensions: [".vcf", ".vcard"],
  },
};

/**
 * @param {string} input
 * @param {string} filename
 * @returns {Object}
 */
export const getMediaTypeInfo = (input, filename = "") => {
  if (!input && !filename) {
    return {
      icon: LucideFile,
      label: "Arquivo",
      type: "file",
    };
  }

  const searchInput = input || filename;
  const lowerInput = searchInput.toLowerCase();

  if (
    lowerInput.includes("ogg") &&
    (lowerInput.includes("opus") || lowerInput.includes("audio"))
  ) {
    return {
      icon: MEDIA_TYPES.voice.icon,
      label: MEDIA_TYPES.voice.label,
      type: "voice",
    };
  }

  if (
    lowerInput.includes("audio/ogg") ||
    (lowerInput.includes("ogg") && lowerInput.includes("audio"))
  ) {
    return {
      icon: MEDIA_TYPES.voice.icon,
      label: MEDIA_TYPES.voice.label,
      type: "voice",
    };
  }

  for (const [type, config] of Object.entries(MEDIA_TYPES)) {
    if (
      config.mimetypes.some((mimetype) =>
        lowerInput.includes(mimetype.toLowerCase())
      )
    ) {
      return {
        icon: config.icon,
        label: config.label,
        type: type,
      };
    }
  }

  for (const [type, config] of Object.entries(MEDIA_TYPES)) {
    if (config.extensions.some((ext) => lowerInput.endsWith(ext))) {
      return {
        icon: config.icon,
        label: config.label,
        type: type,
      };
    }
  }

  return {
    icon: LucideFile,
    label: "Arquivo",
    type: "file",
  };
};

/**
 * @param {string} mediaString
 * @returns {Object}
 */
export const parseMediaString = (mediaString) => {
  if (!mediaString || !mediaString.startsWith("media:")) {
    return null;
  }

  const payload = mediaString.slice(6);
  const parts = payload.split("|");

  if (parts.length >= 1) {
    const typeOrMimetype = parts[0];
    const filenameOrCaption = parts[1] || "";
    const caption = parts[2] || "";

    if (["image", "video", "audio", "document"].includes(typeOrMimetype)) {
      const mediaInfo = MEDIA_TYPES[typeOrMimetype] || MEDIA_TYPES.document;
      return {
        icon: mediaInfo.icon,
        label: mediaInfo.label,
        type: typeOrMimetype,
        caption: caption && caption.trim() ? caption : null,
        filename: filenameOrCaption,
      };
    }

    const mediaInfo = getMediaTypeInfo(typeOrMimetype, filenameOrCaption);
    return {
      icon: mediaInfo.icon,
      label: mediaInfo.label,
      type: mediaInfo.type,
      caption: caption && caption.trim() ? caption : null,
      filename: filenameOrCaption,
      mimetype: typeOrMimetype,
    };
  }

  return null;
};

/**
 * @param {string} type
 * @param {string} mimetypes
 * @param {string} filename
 * @param {string} caption
 * @returns {string}
 */
export const createMediaString = (type, filename = "", caption = "") => {
  const parts = [type];

  if (filename) {
    parts.push(filename);
  }

  if (caption) {
    parts.push(caption);
  }

  return `media:${parts.join("|")}`;
};

const mediaHelper = {
  getMediaTypeInfo,
  parseMediaString,
  createMediaString,
  MEDIA_TYPES,
};

export default mediaHelper;
